<template>
  <div>
    <v-row
      dense
      class="mb-2"
    >
      <v-col
        cols="12"
        sm="5"
      >
        <v-text-field
          v-model="search"
          label="Buscar cupom"
          placeholder="Código..."
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
          @keyup.enter="reload(true)"
          @click:clear="onClear"
        />
      </v-col>
      <v-col
        cols="6"
        sm="2"
      >
        <v-select
          v-model="efeitoFilter"
          :items="efeitoOptions"
          label="Efeito"
          outlined
          dense
          clearable
          hide-details
          @change="reload(true)"
        />
      </v-col>
      <v-col
        cols="6"
        sm="2"
      >
        <v-select
          v-model="ativoFilter"
          :items="ativoOptions"
          label="Status"
          outlined
          dense
          hide-details
          @change="reload(true)"
        />
      </v-col>
      <v-col
        cols="12"
        sm="3"
        class="d-flex align-center justify-md-end"
      >
        <v-btn
          color="secondary"
          small
          block
          @click="openCreate"
        >
          <v-icon
            left
            small
          >
            mdi-plus
          </v-icon>
          Novo cupom
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="total"
      :options.sync="options"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      item-key="id"
      dense
    >
      <template #item.efeito="{ item }">
        <v-chip
          :color="efeitoColor(item.efeito)"
          x-small
          dark
        >
          {{ item.efeito }}
        </v-chip>
      </template>
      <template #item.valor="{ item }">
        {{ formatValor(item) }}
      </template>
      <template #item.ativo="{ item }">
        <v-chip
          :color="item.ativo ? 'success' : 'grey'"
          x-small
          dark
        >
          {{ item.ativo ? 'Ativo' : 'Inativo' }}
        </v-chip>
      </template>
      <template #item.usos="{ item }">
        {{ item.usos_atuais }}{{ item.usos_maximos != null ? ' / ' + item.usos_maximos : '' }}
      </template>
      <template #item.data_validade="{ item }">
        {{ item.data_validade || '—' }}
      </template>
      <template #item.actions="{ item }">
        <v-btn
          icon
          small
          @click="openEdit(item)"
        >
          <v-icon small>
            mdi-pencil
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          color="error"
          :disabled="item.usos_atuais > 0"
          @click="remove(item)"
        >
          <v-icon small>
            mdi-delete
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialog.show"
      max-width="520"
      persistent
    >
      <v-card>
        <v-card-title>{{ dialog.mode === 'create' ? 'Novo cupom' : 'Editar cupom' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="dialog.form.codigo"
            label="Código"
            outlined
            dense
          />
          <v-row dense>
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="dialog.form.efeito"
                :items="efeitoOptions"
                label="Efeito"
                outlined
                dense
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="dialog.form.valor"
                label="Valor"
                type="number"
                step="0.01"
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-row dense>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model="dialog.form.data_validade"
                label="Validade (YYYY-MM-DD)"
                placeholder="2026-12-31"
                outlined
                dense
                clearable
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="dialog.form.usos_maximos"
                label="Usos máximos"
                type="number"
                min="1"
                outlined
                dense
                clearable
              />
            </v-col>
          </v-row>
          <v-switch
            v-model="dialog.form.ativo"
            label="Ativo"
            inset
            dense
            hide-details
          />
          <v-alert
            v-if="dialog.error"
            type="error"
            dense
            outlined
            border="left"
            class="mt-2"
          >
            {{ dialog.error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="dialog.saving"
            @click="dialog.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dialog.saving"
            @click="submit"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
  import { $k } from '@/lib/kraken'

  const EFEITO_OPTIONS = [
    { text: 'Dias grátis', value: 'dias_gratis' },
    { text: 'Percentual', value: 'percentual' },
    { text: 'Valor fixo', value: 'valor_fixo' },
  ]

  const ATIVO_OPTIONS = [
    { text: 'Todos', value: null },
    { text: 'Ativos', value: true },
    { text: 'Inativos', value: false },
  ]

  export default {
    name: 'CouponsSection',

    data: () => ({
      search: '',
      efeitoFilter: null,
      ativoFilter: null,
      items: [],
      total: 0,
      options: { page: 1, itemsPerPage: 10 },
      loading: false,
      headers: [
        { text: 'ID', value: 'id', width: 80 },
        { text: 'Código', value: 'codigo' },
        { text: 'Efeito', value: 'efeito', width: 130 },
        { text: 'Valor', value: 'valor', width: 130 },
        { text: 'Validade', value: 'data_validade', width: 130 },
        { text: 'Usos', value: 'usos', width: 110 },
        { text: 'Status', value: 'ativo', width: 100 },
        { text: '', value: 'actions', width: 110, sortable: false, align: 'end' },
      ],
      efeitoOptions: EFEITO_OPTIONS,
      ativoOptions: ATIVO_OPTIONS,
      dialog: {
        show: false,
        mode: 'create',
        saving: false,
        error: '',
        editingId: null,
        form: { codigo: '', efeito: 'percentual', valor: 0, data_validade: null, usos_maximos: null, ativo: true },
      },
      snackbar: { show: false, color: 'success', text: '' },
    }),

    watch: {
      options: {
        deep: true,
        handler () { this.reload() },
      },
    },

    mounted () {
      this.reload(true)
    },

    methods: {
      msgFromError (err, fallback) {
        return err?.response?.data?.messages?.error?.[0] ||
          err?.response?.data?.messages?.[0]?.text ||
          err?.message ||
          fallback
      },
      efeitoColor (efeito) {
        switch (efeito) {
          case 'dias_gratis': return 'info'
          case 'percentual': return 'success'
          case 'valor_fixo': return 'primary'
          default: return 'grey'
        }
      },
      formatValor (item) {
        const v = Number(item.valor)
        if (Number.isNaN(v)) return item.valor
        if (item.efeito === 'dias_gratis') return `${v} dias`
        if (item.efeito === 'percentual') return `${v}%`
        return `R$ ${v.toFixed(2).replace('.', ',')}`
      },
      onClear () {
        this.search = ''
        this.reload(true)
      },
      async reload (resetPage = false) {
        if (resetPage) this.options = { ...this.options, page: 1 }
        this.loading = true
        try {
          const skip = (this.options.page - 1) * this.options.itemsPerPage
          const payload = { skip, limit: this.options.itemsPerPage }
          if (this.search) payload.q = this.search
          if (this.efeitoFilter) payload.efeito = this.efeitoFilter
          if (this.ativoFilter !== null) payload.ativo = this.ativoFilter
          const res = await $k.manage.clinic_one.coupons.get(payload)
          this.items = res.results || []
          this.total = res.total || 0
        } catch (err) {
          console.error('[coupons.get]', err)
        } finally {
          this.loading = false
        }
      },
      openCreate () {
        this.dialog = {
          show: true,
          mode: 'create',
          saving: false,
          error: '',
          editingId: null,
          form: { codigo: '', efeito: 'percentual', valor: 0, data_validade: null, usos_maximos: null, ativo: true },
        }
      },
      openEdit (item) {
        this.dialog = {
          show: true,
          mode: 'edit',
          saving: false,
          error: '',
          editingId: item.id,
          form: {
            codigo: item.codigo,
            efeito: item.efeito,
            valor: Number(item.valor),
            data_validade: item.data_validade,
            usos_maximos: item.usos_maximos,
            ativo: item.ativo,
          },
        }
      },
      async submit () {
        const { mode, form, editingId } = this.dialog
        if (!form.codigo?.trim()) {
          this.dialog.error = 'Código é obrigatório.'
          return
        }
        this.dialog.saving = true
        this.dialog.error = ''
        try {
          const body = {
            codigo: form.codigo.trim(),
            efeito: form.efeito,
            valor: Number(form.valor) || 0,
            data_validade: form.data_validade || null,
            usos_maximos: form.usos_maximos != null && form.usos_maximos !== '' ? Number(form.usos_maximos) : null,
            ativo: form.ativo,
          }
          if (mode === 'create') {
            await $k.manage.clinic_one.coupons.post(body)
          } else {
            await $k.manage.clinic_one.coupons._id.put({ id: editingId, ...body })
          }
          this.snackbar = { show: true, color: 'success', text: 'Cupom salvo.' }
          this.dialog.show = false
          await this.reload()
        } catch (err) {
          console.error('[coupons.submit]', err)
          this.dialog.error = this.msgFromError(err, 'Falha ao salvar.')
        } finally {
          this.dialog.saving = false
        }
      },
      async remove (item) {
        if (!confirm(`Excluir o cupom "${item.codigo}"?`)) return
        try {
          await $k.manage.clinic_one.coupons._id.delete({ id: item.id })
          this.snackbar = { show: true, color: 'success', text: 'Cupom excluído.' }
          await this.reload()
        } catch (err) {
          console.error('[coupons.delete]', err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao excluir.') }
        }
      },
    },
  }
</script>
